import { useCallback, useState } from "react";
import type {
  AudioChannels,
  AudioSampleRate,
  ConversionSettings,
  ConversionStatus,
  ConvertedFile,
  TargetMimeType,
} from "../types";
import {
  convertAudio,
  getExtensionFromMime,
  getOutputName,
  getUnsupportedConversionReason,
} from "../utils/audioOps";
import { triggerDownload } from "../utils/download";

const INITIAL_STATUS: ConversionStatus = { type: null, message: null };

export const useBatchConvert = (files: File[]) => {
  const [targetFormat, setTargetFormat] =
    useState<TargetMimeType>("audio/mpeg");
  const [bitrateKbps, setBitrateKbps] = useState(192);
  const [sampleRate, setSampleRate] = useState<AudioSampleRate>("original");
  const [channels, setChannels] = useState<AudioChannels>("original");
  const [isConverting, setIsConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<ConversionStatus>(INITIAL_STATUS);
  const [converted, setConverted] = useState<ConvertedFile[]>([]);

  const clearStatus = useCallback(() => setStatus(INITIAL_STATUS), []);

  const clearConverted = useCallback(() => {
    setConverted((prev) => {
      prev.forEach((file) => {
        URL.revokeObjectURL(file.url);
      });
      return [];
    });
  }, []);

  const handleConvert = useCallback(async () => {
    if (!files.length) return;
    const unsupportedReason = getUnsupportedConversionReason(
      files,
      targetFormat,
    );
    if (unsupportedReason) {
      setStatus({ type: "error", message: unsupportedReason });
      return;
    }
    setIsConverting(true);
    setProgress(0);
    setStatus(INITIAL_STATUS);
    clearConverted();

    const results: ConvertedFile[] = [];
    const errors: string[] = [];
    const settings: ConversionSettings = { bitrateKbps, sampleRate, channels };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const blob = await convertAudio(
          file,
          targetFormat,
          settings,
          (fileProgress) => {
            setProgress(
              Math.round(((i + fileProgress / 100) / files.length) * 100),
            );
          },
        );
        const ext = getExtensionFromMime(targetFormat);
        const outName = getOutputName(file.name, targetFormat);
        results.push({
          name: outName,
          originalName: file.name,
          blob,
          url: URL.createObjectURL(blob),
          ext,
          size: blob.size,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Unknown conversion error.";
        errors.push(message);
        console.warn("Skipping file:", message);
      }
      setProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setConverted(results);

    if (results.length === 0) {
      setStatus({
        type: "error",
        message: "No files could be converted. Check console for details.",
      });
    } else {
      const msg =
        results.length === files.length
          ? `All ${results.length} audio file${results.length > 1 ? "s" : ""} converted!`
          : `Converted ${results.length} of ${files.length}. ${errors.length} skipped.`;
      setStatus({ type: "success", message: msg });
    }
    setIsConverting(false);
  }, [bitrateKbps, channels, clearConverted, files, sampleRate, targetFormat]);

  const downloadSingle = useCallback(async (item: ConvertedFile) => {
    await triggerDownload(item.url, item.name, item.blob);
  }, []);

  const downloadAll = useCallback(async () => {
    if (!converted.length) return;
    if (converted.length === 1) {
      await downloadSingle(converted[0]);
      return;
    }

    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();
    const folder = zip.folder("AudioBatch_Converted");
    converted.forEach((file) => {
      folder?.file(file.name, file.blob);
    });
    const content = await zip.generateAsync({ type: "blob" });
    const zipUrl = URL.createObjectURL(content);
    await triggerDownload(zipUrl, `AudioBatch_${Date.now()}.zip`, content);
    setTimeout(() => URL.revokeObjectURL(zipUrl), 10000);
  }, [converted, downloadSingle]);

  return {
    targetFormat,
    setTargetFormat,
    bitrateKbps,
    setBitrateKbps,
    sampleRate,
    setSampleRate,
    channels,
    setChannels,
    isConverting,
    progress,
    status,
    clearStatus,
    converted,
    clearConverted,
    handleConvert,
    downloadAll,
    downloadSingle,
  };
};
